// iter v28 r282 — Componente camera "in-page" per scatto foto durante
// sopralluogo. Su iOS Chrome il file picker nativo (capture="environment")
// non riusciva a aprire la camera mentre il microfono era attivo: schermo
// nero garantito. Soluzione: niente picker nativo, usiamo getUserMedia()
// + <video> + canvas.toBlob(). Funziona su iOS Safari/Chrome, Android, desktop.
//
// Contract:
//   <InAppCameraCapture
//     open={bool}
//     onClose={() => void}
//     onCapture={(File) => Promise<void>}   // chiamato con File JPEG
//   />
//
// Il componente:
//   - Apre stream video facingMode='environment' (camera posteriore)
//   - Mostra live preview a tutto schermo
//   - Bottoni: switch fronte/retro, scatta, annulla
//   - Su scatto: snapshot via canvas → File blob → callback
//   - Pulisce stream alla chiusura
import { useEffect, useRef, useState } from "react";
import { Camera, ArrowsClockwise, X, CheckCircle } from "@phosphor-icons/react";

export default function InAppCameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  // iter v32 r48 · User M1459 — Trigger di restart dello stream usato dal
  // safety-net in `retake()` quando lo stream è stato terminato da iOS
  // (es. app andata in background). Incrementare questa key fa ri-girare
  // `useEffect` su `[open, facingMode, restartKey]` → startStream() riparte.
  const [restartKey, setRestartKey] = useState(0);
  // iter v32 r48 · User M1460 — Flag che diventa true quando `startStream`
  // ha completato e ha assegnato streamRef.current. Serve ai watcher
  // (`ended` listener, visibility recovery, counter reset) per attaccarsi
  // SOLO quando lo stream è davvero pronto. Senza questo, i watcher
  // potrebbero girare prima che startStream finisca (è async) e non
  // attaccare mai i listener.
  const [streamReady, setStreamReady] = useState(0);

  // Avvia / cambia stream camera
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setPreviewBlob(null);
    setPreviewUrl("");

    const startStream = async () => {
      // Cleanup vecchio stream se presente (su switch camera)
      try {
        const old = streamRef.current;
        if (old) {
          old.getTracks().forEach((t) => { try { t.stop(); } catch { /* no-op */ } });
          streamRef.current = null;
        }
      } catch { /* no-op */ }

      if (!navigator.mediaDevices?.getUserMedia) {
        setError("La fotocamera non è disponibile su questo dispositivo.");
        return;
      }

      try {
        // iter v28 r382 — User M1292: «le foto da cellulare nel sopralluogo
        // sono bassissime di qualità, alziamole un minimo». Default
        // getUserMedia rendeva tipicamente 640×480 o 1280×720. Forziamo la
        // risoluzione massima disponibile: la camera posteriore iPhone
        // moderno gestisce 4032×3024 (12 MP). `ideal` lascia al browser
        // negoziare la migliore qualità possibile senza errori se la
        // camera non arriva a tanto.
        const constraints = {
          video: {
            facingMode: { ideal: facingMode },
            width:  { ideal: 4096 },
            height: { ideal: 3072 },
          },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.muted = true;
          try { await videoRef.current.play(); } catch { /* autoplay refuso → user-tap su Scatta */ }
        }
        // iter v32 r48 · User M1460 — segnala ai watcher che lo stream
        // è pronto, così possono attaccare i listener `ended`.
        setStreamReady((k) => k + 1);
      } catch (e) {
        // iter v28 r284 — Messaggi errore specifici. Su iOS Chrome il
        // permesso fotocamera va abilitato manualmente nelle impostazioni
        // di iOS perché Chrome non può richiederlo via prompt come Safari.
        const isIOSChrome = /CriOS/i.test(navigator.userAgent);
        const isIOSSafari = /iPhone|iPad/i.test(navigator.userAgent) && !isIOSChrome;
        let msg = "";
        if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
          if (isIOSChrome) {
            msg = "Permesso fotocamera negato. Su iPhone vai in Impostazioni iOS → Chrome → Fotocamera e attiva. Poi ricarica questa pagina.";
          } else if (isIOSSafari) {
            msg = "Permesso fotocamera negato. Su iPhone vai in Impostazioni iOS → Safari → Fotocamera → \"Chiedi\" o \"Consenti\". Poi ricarica.";
          } else {
            msg = "Permesso fotocamera negato. Clicca sul lucchetto a sinistra dell'URL e consenti la fotocamera.";
          }
        } else if (e?.name === "NotFoundError" || e?.name === "DevicesNotFoundError") {
          msg = "Nessuna fotocamera trovata su questo dispositivo.";
        } else if (e?.name === "NotReadableError") {
          msg = "La fotocamera è già in uso da un'altra app. Chiudila e riprova.";
        } else {
          msg = e?.message || "Impossibile attivare la fotocamera.";
        }
        setError(msg);
      }
    };
    startStream();

    return () => {
      cancelled = true;
    };
  }, [open, facingMode, restartKey]);

  // Cleanup definitivo alla chiusura
  useEffect(() => {
    if (open) return;
    try {
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach((t) => { try { t.stop(); } catch { /* no-op */ } });
        streamRef.current = null;
      }
    } catch { /* no-op */ }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [open]);

  // iter v32 r48 · User M1460 — Crash watcher: monta listener `ended` su
  // ogni track del MediaStream. Su iOS Safari/Chrome le track si possono
  // terminare in modo silenzioso quando:
  //  · l'app va in background
  //  · iOS sospende la camera per risparmio energia
  //  · un'altra app prende il controllo (es. WhatsApp/FaceTime)
  // In tutti questi casi la live preview diventa nera senza mostrare
  // errori. Il watcher rileva l'evento `ended` e ri-attiva
  // automaticamente lo stream incrementando `restartKey` (al massimo
  // 2 retry consecutivi per evitare loop). Se anche il retry fallisce,
  // mostriamo un errore chiaro con il bottone Riprova.
  const autoRetryAttempts = useRef(0);
  useEffect(() => {
    if (!open) return;
    const s = streamRef.current;
    if (!s) return;

    let recoveryTimer = null;
    const tracks = s.getTracks();

    const handleEnded = () => {
      // Evita di reagire se l'utente sta già guardando la preview.
      if (previewBlob) return;
      if (autoRetryAttempts.current >= 2) {
        setError(
          "La fotocamera si è interrotta. Tocca \"Riprova\" per riattivarla."
        );
        return;
      }
      autoRetryAttempts.current += 1;
      // Piccolo debounce per evitare di ripartire mentre iOS sta ancora
      // rilasciando le risorse hardware.
      recoveryTimer = setTimeout(() => {
        setError("");
        setRestartKey((k) => k + 1);
      }, 250);
    };

    tracks.forEach((t) => t.addEventListener("ended", handleEnded));

    return () => {
      tracks.forEach((t) => t.removeEventListener("ended", handleEnded));
      if (recoveryTimer) clearTimeout(recoveryTimer);
    };
  }, [open, restartKey, previewBlob, streamReady]);

  // iter v32 r48 · User M1460 — Watcher complementare: quando la tab torna
  // visibile (es. user rientra nell'app dopo aver risposto a una chiamata)
  // e lo stream è scaduto, lo rilanciamo. iOS spesso sospende WebRTC quando
  // si lock-screena.
  useEffect(() => {
    if (!open) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const s = streamRef.current;
      if (!s) return;
      const allEnded = s.getTracks().every((t) => t.readyState === "ended");
      if (allEnded && !previewBlob) {
        autoRetryAttempts.current = 0; // reset perché è una situazione "fresca"
        setRestartKey((k) => k + 1);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [open, previewBlob, streamReady]);

  // Reset autoRetry counter quando lo stream torna sano
  useEffect(() => {
    if (!open) return;
    const s = streamRef.current;
    if (!s) return;
    const allLive = s.getTracks().every((t) => t.readyState === "live");
    if (allLive) autoRetryAttempts.current = 0;
  }, [open, restartKey, streamReady]);


  const takeSnapshot = async () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    setBusy(true);
    try {
      // iter v28 r382 — User M1292: alziamo la qualità dello scatto.
      // Preferiamo la risoluzione VERA della track (rispetto a v.videoWidth
      // che è quella renderizzata, spesso ridotta dal layout responsivo).
      // Fallback a videoWidth/Height se la track non espone settings.
      let w = v.videoWidth;
      let h = v.videoHeight;
      try {
        const trackSettings = streamRef.current?.getVideoTracks?.()[0]?.getSettings?.();
        if (trackSettings?.width && trackSettings?.height) {
          w = trackSettings.width;
          h = trackSettings.height;
        }
      } catch { /* no-op: usiamo videoWidth/Height */ }
      // iter v28 r383 — User M1293: confermata compressione client-side a
      // 2000px. La camera scatta full-res (4032×3024 iPhone), poi scaliamo
      // il lato lungo a 2000px prima di codificare in JPEG. Risultato:
      // foto da ~3-4 MB → ~600-900 KB, qualità visiva indistinguibile,
      // upload 5x più veloce su 4G. Lato AI enhance non cambia niente
      // perché il backend comunque ridimensiona a 1920px prima di processare.
      const MAX_LONG_EDGE = 2000;
      const longest = Math.max(w, h);
      let outW = w;
      let outH = h;
      if (longest > MAX_LONG_EDGE) {
        const scale = MAX_LONG_EDGE / longest;
        outW = Math.round(w * scale);
        outH = Math.round(h * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      // Smoothing alto per downscale di qualità
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(v, 0, 0, outW, outH);
      // qualità JPEG 0.88 (era 0.95) — al downscale si guadagna nitidezza
      // percepita ma si può abbassare la quality JPEG senza visual loss.
      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.88)
      );
      if (!blob) throw new Error("Snapshot fallito");
      // Mostra preview "Riprova / Usa"
      const url = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl(url);
    } catch (e) {
      setError(e?.message || "Errore scatto");
    } finally {
      setBusy(false);
    }
  };

  const confirmPhoto = async () => {
    if (!previewBlob) return;
    setBusy(true);
    try {
      const file = new File(
        [previewBlob],
        `sopralluogo-${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );
      await onCapture?.(file);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Errore upload");
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl("");
    setError("");
    // iter v32 r48 · User M1460 — il retake manuale azzera il contatore
    // così se l'utente prova di nuovo dopo un'interruzione lunga, gli
    // diamo 2 nuovi tentativi automatici prima di mostrare errore.
    autoRetryAttempts.current = 0;
    // iter v32 r48 · User M1459 — safety net: rilego lo stream al
    // <video> nel caso fosse stato perso (es. tab in background +
    // sospensione iOS), e forzo il replay. Senza questo, dopo "Riprova"
    // si vedeva schermo nero perché il browser ha messo in pausa la
    // sorgente e il srcObject era stato dereferenziato.
    const v = videoRef.current;
    const s = streamRef.current;
    if (v) {
      if (s && (!v.srcObject || v.srcObject !== s)) {
        try { v.srcObject = s; } catch { /* no-op */ }
      }
      try { v.play(); } catch { /* no-op */ }
    }
    // Se per qualunque motivo lo stream non c'è più (track stoppata da
    // iOS in background) ri-attiviamo `startStream` togglando un trigger.
    if (!s || s.getTracks().every((t) => t.readyState === "ended")) {
      setRestartKey((k) => k + 1);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black"
      data-testid="inapp-camera-capture"
    >
      {/* iter v32 r48 · User M1459 — il <video> resta SEMPRE montato.
          Quando c'è una preview (post-scatto) lo nascondiamo con
          display:none invece che con un conditional render. Motivo: se
          smontiamo/rimontiamo il <video>, il videoRef cambia, srcObject
          si perde e dopo "Riprova" si vede uno schermo nero. */}
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        playsInline
        autoPlay
        muted
        data-testid="inapp-camera-video"
        style={{ display: previewBlob ? "none" : "block" }}
      />

      {/* Snapshot preview */}
      {previewBlob && previewUrl && (
        <img
          src={previewUrl}
          alt="Anteprima foto"
          className="absolute inset-0 w-full h-full object-contain bg-black"
          data-testid="inapp-camera-preview"
        />
      )}

      {/* Top bar: chiudi + switch camera */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
        <button
          type="button"
          onClick={onClose}
          className="text-white p-2 rounded-full hover:bg-white/10 active:bg-white/20 transition"
          aria-label="Chiudi"
          data-testid="inapp-camera-close"
        >
          <X size={26} weight="bold" />
        </button>
        {!previewBlob && (
          <button
            type="button"
            onClick={() => setFacingMode((m) => (m === "environment" ? "user" : "environment"))}
            className="text-white p-2 rounded-full hover:bg-white/10 active:bg-white/20 transition"
            aria-label="Cambia camera"
            data-testid="inapp-camera-switch"
          >
            <ArrowsClockwise size={24} weight="bold" />
          </button>
        )}
      </div>

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-8 bg-black/85">
          <div className="bg-white rounded-2xl p-6 max-w-sm text-center">
            <div className="text-[#3F3D38] text-[14px] leading-relaxed mb-4">{error}</div>
            <div className="flex items-center justify-center gap-3">
              {/* iter v32 r48 · User M1460 — pulsante Riprova nel pannello
                  errore: ri-attiva lo stream camera senza chiudere il
                  modale, utile per la maggior parte degli errori transienti
                  (camera occupata, sospensione iOS, ecc). */}
              <button
                type="button"
                onClick={() => {
                  setError("");
                  autoRetryAttempts.current = 0;
                  setRestartKey((k) => k + 1);
                }}
                className="px-5 py-2 bg-[#7C8B6E] text-white rounded-full text-[13px] font-medium"
                data-testid="inapp-camera-error-retry"
              >
                Riprova
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 bg-white border border-[#E5E1D5] text-[#1C1C1A] rounded-full text-[13px] font-medium"
                data-testid="inapp-camera-error-close"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom bar: scatta / riprova / conferma */}
      {!error && (
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-10 bg-gradient-to-t from-black/80 to-transparent">
          {!previewBlob ? (
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={takeSnapshot}
                disabled={busy}
                className="w-20 h-20 rounded-full bg-white border-4 border-[#7C8B6E] flex items-center justify-center shadow-lg active:scale-95 transition disabled:opacity-50"
                aria-label="Scatta foto"
                data-testid="inapp-camera-shutter"
              >
                <Camera size={32} className="text-[#7C8B6E]" weight="regular" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={retake}
                disabled={busy}
                className="px-5 py-3 bg-white/15 text-white rounded-full text-[14px] font-medium backdrop-blur-md hover:bg-white/25 transition disabled:opacity-50"
                data-testid="inapp-camera-retake"
              >
                Riprova
              </button>
              <button
                type="button"
                onClick={confirmPhoto}
                disabled={busy}
                className="px-6 py-3 bg-[#7C8B6E] text-white rounded-full text-[14px] font-semibold flex items-center gap-2 shadow-lg active:scale-95 transition disabled:opacity-50"
                data-testid="inapp-camera-confirm"
              >
                <CheckCircle size={18} weight="bold" />
                {busy ? "Carico..." : "Usa questa"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
